import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { useTableStateBag } from '@work4all/components';
import { SupplierGroupPicker } from '@work4all/components/lib/components/entity-picker/entity-group-picker/EntityGroupPicker';

import { useEntityEvents } from '@work4all/data/lib/entity-events/use-entity-events';

import { Category } from '@work4all/models/lib/Classes/Category.entity';
import { Group } from '@work4all/models/lib/Classes/Group.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Table } from '../../components/data-tables/table/Table';
import { SupplierFileInfoPanel } from '../../containers/files/detail/components/file-info-panel/SupplierFileInfoPanel';
import { useSupplier } from '../../containers/files/detail/components/queries/use-supplier';
import { PersonalAndGeneralView } from '../filters/PersonalAndGeneralView';
import { SupplierCategoryPicker } from '../filters/SupplierCategoryPicker';

import { ClosedStatusQuickFilter } from './ClosedStatusQuickFilter';
import { EntityTable } from './EntityTable';
import { FileActions, useFileActions } from './FileActions';
import { PreviewMobileWrapper } from './PreviewMobileWrapper';
import schema from './schemata/supplier-table-schema.json';
import { TableLayoutContext, useTableLayoutState } from './table-layout';
import {
  TableQuickFilters,
  TableQuickFiltersSection,
  TableQuickFiltersSectionHeader,
} from './table-quick-filters';
import { TableNoRowsPlaceholder } from './TableNoRowsPlaceholder';
import { useDataTable } from './use-data-table';
import { useDeleteEntitiesToolbarConfig } from './use-delete-entities-toolbar-config';
import { useTableMaskHandlers } from './use-table-mask-handlers';
import { useUserColumnConfigs } from './use-user-column-configs';

type Props = EntityTable;

type ISupplierTableCustomColumns = Record<string, never>;

const defaultSort = [{ field: 'name', direction: SortDirection.ASCENDING }];
const manualGroupBy = true;

export const SuppliersTable = forwardRef<TableInstance, Props>(
  function SuppliersTable(props, ref) {
    const { prefilter, onOpenMask, ...rest } = props;
    const { entity: entityType } = schema as never;

    const tableStateBag = useTableStateBag();

    const layoutState = useTableLayoutState();
    const [layout] = layoutState;

    const {
      columnConfigs,
      cardConfig,
      prepareRowDisplayModifiers,
      data,
      fetchMore,
      total,
      selectedEntity,
      initialSortBy,
      pending,
    } = useDataTable<Supplier, ISupplierTableCustomColumns>({
      layout,
      schema: schema as never,
      tableStateBag,
      prefilter,
      defaultSort,
    });

    const { supplier: fullyLoadedSupplier, refetch } = useSupplier(
      selectedEntity?.id.toString()
    );

    const maskHandlers = useTableMaskHandlers(entityType, onOpenMask);

    const deleteConfig = useDeleteEntitiesToolbarConfig({
      entityType,
    });

    const [userConfig, userConfigMethods] = useUserColumnConfigs({
      layout,
      entityType,
      columnConfigs,
    });

    useEntityEvents((event) => {
      if (event.entity === Entities.contact) {
        refetch();
      }
    });

    const rowModifiers = useCallback(
      (value) => {
        const modifiers = prepareRowDisplayModifiers(value);
        return { ...modifiers, isFaded: value.extinct === -1 };
      },
      [prepareRowDisplayModifiers]
    );

    const { fileActionDefinitions } = useFileActions({
      entity: Entities.supplier,
    });

    if (!userConfig) return null;

    return (
      <TableLayoutContext value={layoutState}>
        <Table
          pending={pending}
          noRowsRenderer={() => <TableNoRowsPlaceholder />}
          ref={ref}
          layout={layout}
          cardConfig={cardConfig}
          areas={{
            left: {
              content: <SuppliersTableQuickFilters />,
              resizable: true,
            },
            right: selectedEntity !== null && {
              content: (
                <PreviewMobileWrapper
                  active={true}
                  actions={{
                    edit: maskHandlers.edit,
                    remove: deleteConfig,
                    additionalActionsLeft: (
                      <FileActions entity={Entities.supplier} />
                    ),
                  }}
                >
                  <SupplierFileInfoPanel
                    supplier={fullyLoadedSupplier}
                    onCloseClicked={() =>
                      tableStateBag.tableInstance.toggleAllRowsSelected(false)
                    }
                    onEdit={maskHandlers.edit?.handler}
                    onAddContact={() => {
                      onOpenMask().handler({
                        entity: Entities.contact,
                        template: {
                          entity: Entities.supplier,
                          id: String(selectedEntity.id),
                        },
                      });
                    }}
                    onEditContact={(contactId) => {
                      onOpenMask().handler({
                        entity: Entities.contact,
                        id: String(contactId),
                        template: {
                          entity: Entities.supplier,
                          id: String(selectedEntity.id),
                        },
                      });
                    }}
                  />
                </PreviewMobileWrapper>
              ),
            },
          }}
          actions={{
            add: maskHandlers.create,
            edit: maskHandlers.edit,
            convert: {
              exclude: entityType,
              handler: maskHandlers.convert,
            },
            remove: deleteConfig,
            resetColumns: userConfigMethods.remove,
            custom: {
              left: fileActionDefinitions,
            },
          }}
          columnConfigs={userConfig}
          manualGroupBy={manualGroupBy}
          initialSortBy={initialSortBy}
          loadMoreItems={fetchMore}
          prepareRowDisplayModifiers={rowModifiers}
          data={data}
          total={total}
          {...rest}
        />
      </TableLayoutContext>
    );
  }
);

function SuppliersTableQuickFilters() {
  const { t } = useTranslation();

  const tableStateBag = useTableStateBag();

  const columns = {
    group: tableStateBag.columnsById['groupId'],
    categories: tableStateBag.columnsById['categoryAssignmentList.categoryId'],
    closed: tableStateBag.columnsById['extinct'],
  };

  const groups = columns.group?.filterValue?.value ?? null;

  const handleGroupsChange = (groups: Group[]) => {
    const column = columns.group;

    if (!column) {
      return;
    }

    if (groups.length === 0) {
      column.setFilter(null);
    } else {
      column.setFilter({
        value: groups,
        filterType: column.filterType,
      });
    }
  };

  const categories = columns.categories?.filterValue?.value ?? null;

  const handleCategoriesChange = (categories: Category[]) => {
    const column = columns.categories;

    if (!column) {
      return;
    }

    if (categories.length === 0) {
      column.setFilter(null);
    } else {
      column.setFilter({
        value: categories,
        filterType: column.filterType,
      });
    }
  };

  return (
    <TableQuickFilters>
      <ClosedStatusQuickFilter
        entityType={Entities.supplier}
        column={columns.closed}
        label={t('SUPPLIER.CLOSED_STATUS.HIDE_CLOSED')}
      />

      <TableQuickFiltersSection>
        <TableQuickFiltersSectionHeader>
          {t('COMMON.GROUPS')}
        </TableQuickFiltersSectionHeader>
        <SupplierGroupPicker value={groups} onChange={handleGroupsChange} />
      </TableQuickFiltersSection>

      <TableQuickFiltersSection>
        <TableQuickFiltersSectionHeader>
          {t('COMMON.CATEGORIES')}
        </TableQuickFiltersSectionHeader>
        <SupplierCategoryPicker
          value={categories}
          onChange={handleCategoriesChange}
        />
      </TableQuickFiltersSection>

      <PersonalAndGeneralView
        value={groups ?? categories}
        entity={Entities.supplier}
      />
    </TableQuickFilters>
  );
}
